import ChoiceList from "components/ChoiceList";
import {
  WizardMaskedField,
  WizardTextField,
  ZipField,
} from "components/WizardFields";

import type { PlanningFor } from "../answers";
import { lovedOneZip } from "../flow";
import { dobError, nameError, zipError } from "../validation";
import { RelationFields } from "./DeathSteps";
import StepFrame, { gated, useContinueGate } from "./StepFrame";
import type { StepProps } from "./types";
import YesNoChoice from "./YesNoChoice";

const PLANNING_FOR_OPTIONS = [
  { value: "self" as const, label: "Myself" },
  { value: "loved_one" as const, label: "A loved one" },
  {
    value: "both" as const,
    label: "Myself and a loved one",
    caption: "Like you and your spouse or partner.",
  },
];

export function PlanningForStep({
  answers,
  update,
  advance,
  advanceSoon,
  onBack,
}: StepProps) {
  return (
    <StepFrame
      answers={answers}
      step="planning-for"
      onBack={onBack}
      onContinue={advance}
      continueDisabled={answers.planningFor === null}
    >
      <ChoiceList<PlanningFor>
        label="Who you are planning for"
        options={PLANNING_FOR_OPTIONS}
        value={answers.planningFor}
        onChoose={(planningFor) => {
          update((current) => ({ ...current, planningFor }));
          advanceSoon();
        }}
      />
    </StepFrame>
  );
}

export function YouStep(props: StepProps) {
  const { answers, update, onBack, checking } = props;
  const { attempted, tryContinue } = useContinueGate(props, "you");
  const set = (key: "firstName" | "lastName" | "dob" | "zip", value: string) =>
    update((current) => ({
      ...current,
      self: { ...current.self, [key]: value },
    }));
  return (
    <StepFrame
      answers={answers}
      step="you"
      onBack={onBack}
      onContinue={tryContinue}
      busy={checking}
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 2xs:grid-cols-2">
          <WizardTextField
            id="you-first-name"
            label="First name"
            value={answers.self.firstName}
            onChange={(value) => set("firstName", value)}
            error={gated(
              attempted,
              nameError(answers.self.firstName, "First name"),
            )}
            autoComplete="given-name"
            autoFocus
          />
          <WizardTextField
            id="you-last-name"
            label="Last name"
            value={answers.self.lastName}
            onChange={(value) => set("lastName", value)}
            error={gated(
              attempted,
              nameError(answers.self.lastName, "Last name"),
            )}
            autoComplete="family-name"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 2xs:grid-cols-2">
          <WizardMaskedField
            kind="dob"
            id="you-dob"
            label="Date of birth"
            value={answers.self.dob}
            onChange={(value) => set("dob", value)}
            error={gated(attempted, dobError(answers.self.dob))}
          />
          <ZipField
            id="you-zip"
            value={answers.self.zip}
            onChange={(value) => set("zip", value)}
            error={gated(attempted, zipError(answers.self.zip))}
          />
        </div>
      </div>
    </StepFrame>
  );
}

export function YouHealthStep({
  answers,
  update,
  advance,
  advanceSoon,
  onBack,
}: StepProps) {
  return (
    <StepFrame
      answers={answers}
      step="you-health"
      onBack={onBack}
      onContinue={advance}
      continueDisabled={answers.self.hospice === null}
    >
      <YesNoChoice
        label="Your life expectancy is 12 months or less, or you are on hospice care"
        value={answers.self.hospice}
        onChoose={(hospice) => {
          update((current) => ({
            ...current,
            self: { ...current.self, hospice },
          }));
          advanceSoon();
        }}
      />
    </StepFrame>
  );
}

export function LovedStep(props: StepProps) {
  const { answers, update, onBack } = props;
  const { attempted, tryContinue } = useContinueGate(props, "loved");
  const set = (key: "firstName" | "lastName", value: string) =>
    update((current) => ({
      ...current,
      lovedOne: { ...current.lovedOne, [key]: value },
    }));
  return (
    <StepFrame
      answers={answers}
      step="loved"
      onBack={onBack}
      onContinue={tryContinue}
    >
      <div className="grid grid-cols-1 gap-4 2xs:grid-cols-2">
        <WizardTextField
          id="loved-first-name"
          label="First name"
          value={answers.lovedOne.firstName}
          onChange={(value) => set("firstName", value)}
          error={gated(
            attempted,
            nameError(answers.lovedOne.firstName, "First name"),
          )}
          autoComplete="off"
          autoFocus
        />
        <WizardTextField
          id="loved-last-name"
          label="Last name"
          value={answers.lovedOne.lastName}
          onChange={(value) => set("lastName", value)}
          error={gated(
            attempted,
            nameError(answers.lovedOne.lastName, "Last name"),
          )}
          autoComplete="off"
        />
      </div>
    </StepFrame>
  );
}

export function LovedHealthStep({
  answers,
  update,
  advance,
  advanceSoon,
  onBack,
}: StepProps) {
  const first = answers.lovedOne.firstName.trim();
  return (
    <StepFrame
      answers={answers}
      step="loved-health"
      onBack={onBack}
      onContinue={advance}
      continueDisabled={answers.lovedOne.hospice === null}
    >
      <YesNoChoice
        label={`${first === "" ? "Your loved one" : first}’s life expectancy is 12 months or less, or they are on hospice care`}
        value={answers.lovedOne.hospice}
        onChoose={(hospice) => {
          update((current) => ({
            ...current,
            lovedOne: { ...current.lovedOne, hospice },
          }));
          advanceSoon();
        }}
      />
    </StepFrame>
  );
}

export function LovedDetailsStep(props: StepProps) {
  const { answers, update, onBack, checking } = props;
  const { attempted, tryContinue } = useContinueGate(props, "loved-details");
  const first = answers.lovedOne.firstName.trim();
  const both = answers.planningFor === "both";
  const sharesZip = both && answers.lovedOne.livesInMyZip;
  const set = (key: "dob" | "zip" | "livesInMyZip", value: string | boolean) =>
    update((current) => ({
      ...current,
      lovedOne: { ...current.lovedOne, [key]: value },
    }));
  return (
    <StepFrame
      answers={answers}
      step="loved-details"
      onBack={onBack}
      onContinue={tryContinue}
      busy={checking}
    >
      <div className="flex flex-col gap-4">
        <WizardMaskedField
          kind="dob"
          id="loved-dob"
          label="Date of birth"
          value={answers.lovedOne.dob}
          onChange={(value) => set("dob", value)}
          error={gated(attempted, dobError(answers.lovedOne.dob))}
        />
        {both && (
          <label className="flex items-center gap-3 text-base font-medium text-soil">
            <input
              type="checkbox"
              checked={answers.lovedOne.livesInMyZip}
              onChange={(event) => set("livesInMyZip", event.target.checked)}
              className="h-5 w-5 rounded border-oat-300 text-evergreen focus:ring-2 focus:ring-evergreen/40"
            />
            {first === "" ? "They live" : `${first} lives`} in my ZIP code
          </label>
        )}
        {!sharesZip && (
          <ZipField
            id="loved-zip"
            label={
              first === ""
                ? "ZIP code where they live"
                : `ZIP code where ${first} lives`
            }
            value={answers.lovedOne.zip}
            onChange={(value) => set("zip", value)}
            error={gated(attempted, zipError(lovedOneZip(answers)))}
          />
        )}
      </div>
    </StepFrame>
  );
}

export function LovedRelationStep(props: StepProps) {
  const { answers, update, onBack, advanceSoon } = props;
  const { attempted, tryContinue } = useContinueGate(props, "loved-relation");
  return (
    <StepFrame
      answers={answers}
      step="loved-relation"
      onBack={onBack}
      onContinue={tryContinue}
      continueDisabled={answers.lovedOne.relation.type === null}
    >
      <RelationFields
        label="How you are related"
        type={answers.lovedOne.relation.type}
        otherText={answers.lovedOne.relation.otherText}
        attempted={attempted}
        onChange={(relation) =>
          update((current) => ({
            ...current,
            lovedOne: { ...current.lovedOne, relation },
          }))
        }
        advanceSoon={advanceSoon}
      />
    </StepFrame>
  );
}
